import Vue from 'vue';
import TYPES from '@/types';
import { currencyFormat } from '@/vue-app/utils/currency';
import { maxValueRule, minValueRule, requiredRule } from '@/vue-app/utils/form-rules';
import { parseCurrencyToNumber } from '@/vue-app/utils/parse-currency-to-number';

// Application
import GetRetirementInvestorGoalCalculatorQuery
  from '@/modules/flagship/retirement-investor-goal-calculator/application/queries/get-retirement-investor-goal-calculator-query';
import CalculateRetirementGoalMinimumMonthlyPensionQuery
  from '@/modules/flagship/retirement-investor-goal-calculator/application/queries/calculate-retirement-goal-minimum-monthly-pension-query';
import EditRetirementFundGoalPlanRetirementAgeService
  from '@/modules/flagship/edit-plan-goals/edit-retirement-fund-goal-plan/application/services/edit-retirement-fund-goal-plan-retirement-age-service';
import EditRetirementFundGoalPlanCurrentAmountService
  from '@/modules/flagship/edit-plan-goals/edit-retirement-fund-goal-plan/application/services/edit-retirement-fund-goal-plan-current-amount-service';
import EditRetirementFundGoalPlanCurrentPlanService
  from '@/modules/flagship/edit-plan-goals/edit-retirement-fund-goal-plan/application/services/edit-retirement-fund-goal-plan-current-plan-service';
import EditRetirementFundGoalPlanDefineByService
  from '@/modules/flagship/edit-plan-goals/edit-retirement-fund-goal-plan/application/services/edit-retirement-fund-goal-plan-define-by-service';

// Domain
import { RetirementInvestorGoalCalculatorEntity } from '@/modules/flagship/retirement-investor-goal-calculator/domain/entities/retirement-investor-goal-calculator-entity';
import { RetirementInvestorGoalCalculatorDto }
  from '@/modules/flagship/retirement-investor-goal-calculator/domain/dtos/retirement-investor-goal-calculator-dto';
import {
  RetirementGoalMinimumMonthlyPensionCalculationDto,
} from '@/modules/flagship/retirement-investor-goal-calculator/domain/dtos/retirement-goal-minimum-monthly-pension-dto';
import {
  CreateInvestorGoalStateManager,
} from '@/modules/flagship/investor-goal/investor_goal/domain/state/create-investor-goal-state-manager';
import Inject from '@/modules/shared/domain/di/inject';
import Translator from '@/modules/shared/domain/i18n/translator';
import { Values } from '@/modules/shared/domain/i18n/types';
import { MessageNotifier } from '@/modules/shared/domain/notifiers/message_notifier';

export default class RetirementFundContributionsViewModel {
  @Inject(TYPES.GET_RETIREMENT_INVESTOR_GOAL_CALCULATOR_QUERY)
  private readonly get_retirement_investor_goal_calculator_query!:
    GetRetirementInvestorGoalCalculatorQuery;

  @Inject(TYPES.CALCULATE_RETIREMENT_GOAL_MINIMUM_MONTHLY_PENSION_QUERY)
  private readonly calculate_minimum_monthly_pension!:
    CalculateRetirementGoalMinimumMonthlyPensionQuery;

  @Inject(TYPES.EDIT_RETIREMENT_FUND_GOAL_PLAN_RETIREMENT_AGE_SERVICE)
  private readonly retirement_age_service!: EditRetirementFundGoalPlanRetirementAgeService;

  @Inject(TYPES.EDIT_RETIREMENT_FUND_GOAL_PLAN_CURRENT_AMOUNT_SERVICE)
  private readonly current_amount_service!: EditRetirementFundGoalPlanCurrentAmountService;

  @Inject(TYPES.EDIT_RETIREMENT_FUND_GOAL_PLAN_CURRENT_PLAN_SERVICE)
  private readonly current_plan_service!: EditRetirementFundGoalPlanCurrentPlanService;

  @Inject(TYPES.EDIT_RETIREMENT_FUND_GOAL_PLAN_DEFINE_BY_SERVICE)
  private readonly define_by_service!: EditRetirementFundGoalPlanDefineByService;

  @Inject(TYPES.CREATE_INVESTOR_GOAL_STATE_MANAGER)
  readonly create_investor_goal_state_manager!: CreateInvestorGoalStateManager;

  @Inject(TYPES.I18N)
  private readonly translator!: Translator;

  @Inject(TYPES.NOTIFIER)
  private readonly message_notifier!: MessageNotifier;

  readonly i18n_namespace = 'components.flagship.flagship-goals.create-goal.retirement-fund.contributions';

  readonly view: Vue;

  public constructor(view: Vue) {
    this.view = view;
  }

  timer?: NodeJS.Timer;

  readonly min_contribution = 500;

  is_valid_form = false;

  is_loading = false;

  retirement_goal_calculator_dto: RetirementInvestorGoalCalculatorDto = {
    associated_product_id: '',
    defined_by: '',
    issued_age: 0,
    initial_amount: 0,
    retirement_age_adjusted: 0,
    monthly_pension_adjusted: 0,
    monthly_required_amount: 0,
    legacy_amount: 0,
  };

  min_pension = 1;

  max_pension = 1000000;

  max_contribution = 105726;

  pension_in_future = '0.0 MXN';

  show_inflation_info_dialog = false;

  input_rules = {
    contribution: [
      requiredRule,
      (value: string) => minValueRule(value.replace(/[^0-9.-]/g, ''), '$500.00 MXN', this.min_contribution),
      (value: string) => maxValueRule(value.replace(/[^0-9.-]/g, ''), '$105726.00 MXN', this.max_contribution),
    ],
    pension: [
      requiredRule,
      (value: string) => minValueRule(value.replace(/[^0-9.-]/g, ''),
        this.getAmountFormatted(this.min_pension), this.min_pension),
      (value: string) => maxValueRule(value.replace(/[^0-9.-]/g, ''),
        this.getAmountFormatted(this.max_pension), this.max_pension),
    ],
  };

  define_by_state = this.define_by_service.getDefineByInformation();

  retirement_age_state = this.retirement_age_service.getRetirementAgeInformation();

  initial_amount_state = this.current_amount_service.getCurrentAmountInformation();

  current_plan_state = this.current_plan_service.getCurrentPlanInformation();

  translate = (message: string, values?: Values) => this.translator.translate(`${this.i18n_namespace}.${message}`, values);

  initialize = async () => {
    await this.setInitialValues();
    await this.calculateMinimumAccumulatedAmount();
    this.delay();
  }

  get target_amount_formatted() {
    return this.getAmountFormatted(this.define_by_state.accumulated_amount);
  }

  get current_amount_formatted() {
    return this.getAmountFormatted(parseCurrencyToNumber(
      this.current_plan_state.current_amount,
    ) + this.initial_amount_state.amount_added);
  }

  get you_must_save() {
    const you_must_save = this.define_by_state.is_defined_by_contribution
      ? this.define_by_state.monthly_desired_pension
      : this.define_by_state.contribution;
    const you_must_save_formatted = parseCurrencyToNumber(you_must_save);
    return this.getAmountFormatted(you_must_save_formatted);
  }

  get is_continue_btn_disabled() {
    return !this.is_valid_form || this.is_loading;
  }

  getCustomClassOpacity = (is_option_by_time: boolean) => {
    if ((this.define_by_state.is_defined_by_contribution && is_option_by_time) || (!this
      .define_by_state.is_defined_by_contribution && !is_option_by_time)) return 'opacity';
    return '';
  }

  getAmountFormatted(amount: number) {
    return currencyFormat(amount);
  }

  delay = () => {
    this.is_loading = true;
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = undefined;
    }
    this.timer = setTimeout(async () => {
      if (this.is_valid_form) {
        await this.loadMonthlyRequiredAmount();
      } else {
        this.is_loading = false;
      }
    }, 2000);
  }

  setInitialValues = async () => {
    try {
      this.retirement_goal_calculator_dto = {
        associated_product_id: this.current_plan_state.associated_product_id,
        defined_by: this.define_by_state.defined_by,
        issued_age: this.retirement_age_state.current_age,
        retirement_age_adjusted: this.retirement_age_state.retirement_age,
        initial_amount: parseCurrencyToNumber(this.current_amount_formatted),
        monthly_pension_adjusted: this.define_by_state.monthly_pension_adjusted,
        monthly_required_amount: this.define_by_state.monthly_required_amount,
        legacy_amount: this.define_by_state.legacy_amount,
      };
      await this.loadMonthlyRequiredAmount();
    } catch {
      this.message_notifier.showErrorNotification(this.translate('errors.load_retirement_fund_information'));
    }
  }

  definedByChange = async () => {
    this.define_by_state.defined_by = this.define_by_state
      .is_defined_by_contribution ? 'contribution' : 'pension';
    if (!this.define_by_state.is_defined_by_contribution) {
      this.define_by_state.monthly_pension_adjusted = this.define_by_state
        .without_inflation.monthly_pension;
      this.define_by_state.monthly_desired_pension = this.define_by_state
        .without_inflation.monthly_pension.toString();
    } else if (this.define_by_state.calculate_inflation) {
      this.define_by_state.monthly_required_amount = this.define_by_state
        .with_inflation.monthly_required_amount;
      this.define_by_state.contribution = this.define_by_state
        .with_inflation.monthly_required_amount.toString();
    } else {
      this.define_by_state.monthly_required_amount = this.define_by_state
        .without_inflation.monthly_required_amount;
      this.define_by_state.contribution = this.define_by_state
        .without_inflation.monthly_required_amount.toString();
    }
    await this.loadMonthlyRequiredAmount();
  }

  calculateMinimumAccumulatedAmount = async () => {
    try {
      if (parseCurrencyToNumber(this.current_amount_formatted) > 0) {
        const interest_rate = this.current_plan_state.associated_product_interest_rate / 100;
        const payload: RetirementGoalMinimumMonthlyPensionCalculationDto = {
          initial_amount: parseCurrencyToNumber(this.current_amount_formatted),
          monthly_required_amount: 0,
          retirement_range_adjusted: this.retirement_age_state.retirement * 12,
          pension_range_adjusted: this.retirement_age_state.pension * 12,
          interest_rate,
        };
        const {
          minimum_monthly_pension,
        } = await this.calculate_minimum_monthly_pension.execute(payload);
        this.min_pension = Math.ceil(parseFloat(minimum_monthly_pension));
      } else {
        this.min_pension = 1;
      }
    } catch {
      this.message_notifier.showErrorNotification(this.translate('errors.calculate_minimum_accumulated_amount'));
    } finally {
      this.is_loading = false;
    }
  }

  setValuesWithAndWithoutInflation = (
    { retirement_calculation_data }: RetirementInvestorGoalCalculatorEntity,
  ) => {
    this.define_by_state.with_inflation.monthly_pension = retirement_calculation_data
      .monthly_pension_with_inflation;
    this.define_by_state.with_inflation.monthly_required_amount = retirement_calculation_data
      .monthly_required_amount_with_inflation;
    this.define_by_state.without_inflation.accumulated_amount = retirement_calculation_data
      .accumulated_amount_without_inflation;
    if (this.define_by_state.is_defined_by_contribution) {
      if (!this.define_by_state.calculate_inflation) {
        this.define_by_state.without_inflation.monthly_pension = retirement_calculation_data
          .monthly_pension_without_inflation;
        this.pension_in_future = this.getAmountFormatted(
          retirement_calculation_data.pension_desired_in_future_without_inflation,
        );
      } else {
        this.define_by_state.without_inflation.monthly_pension = retirement_calculation_data
          .pension_desired_in_future_with_inflation;
        this.pension_in_future = this.getAmountFormatted(
          retirement_calculation_data.pension_desired_in_future_with_inflation,
        );
      }
    } else {
      this.define_by_state.without_inflation.monthly_pension = retirement_calculation_data
        .monthly_pension_without_inflation;
    }
    this.define_by_state.without_inflation.monthly_required_amount = retirement_calculation_data
      .monthly_required_amount_without_inflation;
    this.define_by_state.with_inflation.pension_desired_in_future = retirement_calculation_data
      .pension_desired_in_future_with_inflation;
    this.define_by_state.without_inflation.pension_desired_in_future = retirement_calculation_data
      .pension_desired_in_future_without_inflation;
    this.define_by_state.percentage_decrease = retirement_calculation_data.percentage_decrease;
  }

  calculateRetirementPlan = async () => {
    let monthly_pension_adjusted = 0;
    let monthly_required_amount = 0;
    let accumulated_amount = 0;

    try {
      this.retirement_goal_calculator_dto.defined_by = this.define_by_state
        .is_defined_by_contribution ? 'contribution' : 'pension';
      if (this.define_by_state.is_defined_by_contribution) {
        this.retirement_goal_calculator_dto.monthly_pension_adjusted = 0;
        this.retirement_goal_calculator_dto.monthly_required_amount = parseCurrencyToNumber(
          this.define_by_state.contribution,
        );
      } else {
        this.retirement_goal_calculator_dto.monthly_pension_adjusted = parseCurrencyToNumber(
          this.define_by_state.monthly_desired_pension,
        );
        this.retirement_goal_calculator_dto.monthly_required_amount = 0;
      }

      const retirement_investor_goal_calculator_entity = await this
        .get_retirement_investor_goal_calculator_query.execute(this.retirement_goal_calculator_dto);

      const { retirement_calculation_data } = retirement_investor_goal_calculator_entity;

      if (!this.define_by_state.calculate_inflation) {
        monthly_required_amount = retirement_calculation_data
          .monthly_required_amount_without_inflation;
        monthly_pension_adjusted = retirement_calculation_data.monthly_pension_without_inflation;
        accumulated_amount = retirement_calculation_data.accumulated_amount_without_inflation;
      } else {
        monthly_required_amount = retirement_calculation_data
          .monthly_required_amount_with_inflation;
        monthly_pension_adjusted = retirement_calculation_data.monthly_pension_with_inflation;
        accumulated_amount = retirement_calculation_data.accumulated_amount_with_inflation;
      }

      this.setValuesWithAndWithoutInflation(retirement_investor_goal_calculator_entity);

      return {
        monthly_required_amount,
        monthly_pension_adjusted,
        accumulated_amount,
      };
    } catch {
      this.message_notifier.showErrorNotification(this.translate('errors.load_monthly_required_amount'));
      return {
        monthly_required_amount,
        monthly_pension_adjusted,
        accumulated_amount,
      };
    }
  }

  loadMonthlyRequiredAmount = async () => {
    try {
      this.is_loading = true;
      if (parseCurrencyToNumber(this.define_by_state.contribution) !== 0
        || parseCurrencyToNumber(this.define_by_state.monthly_desired_pension) !== 0) {
        const {
          monthly_required_amount,
          monthly_pension_adjusted,
          accumulated_amount,
        } = await this.calculateRetirementPlan();
        if (this.define_by_state.is_defined_by_contribution) {
          this.define_by_state.monthly_desired_pension = monthly_pension_adjusted
            .toString();
        } else {
          this.define_by_state.contribution = monthly_required_amount.toString();
        }
        this.define_by_state.accumulated_amount = accumulated_amount;
      } else {
        this.define_by_state.monthly_desired_pension = '0';
        this.define_by_state.contribution = '0';
        this.define_by_state.accumulated_amount = 0;
      }
    } catch {
      this.message_notifier.showErrorNotification(this.translate('errors.load_monthly_required_amount'));
    } finally {
      this.is_loading = false;
    }
  };

  setState = async () => {
    this.define_by_state.monthly_required_amount = parseCurrencyToNumber(this
      .define_by_state.contribution);
    this.define_by_state.monthly_pension_adjusted = parseCurrencyToNumber(this
      .define_by_state.monthly_desired_pension);
    this.define_by_state.calculated_pension = parseCurrencyToNumber(this
      .define_by_state.monthly_desired_pension);
  }

  prevStep = () => {
    this.view.$emit('prevStep');
  }

  nextStep = async () => {
    try {
      await this.setState();
      this.view.$emit('nextStep');
    } catch {
      this.message_notifier.showErrorNotification(this.translate('errors.save'));
    }
  }

  showInflationInfo = () => {
    this.show_inflation_info_dialog = true;
  }
}
